import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';

import { trackConversion } from '../hooks/usePageTracking';

const SubscriptionComplete = () => {
  const [currentMessage, setCurrentMessage] = useState(0);
  const [progress, setProgress] = useState(0);
  const navigate = useNavigate();

  const messages = [
    "🧬 Initializing your AI assistant...",
    "🎭 Teaching your assistant about your business...",
    "📞 Setting up your phone number...",
    "🗓️ Configuring your calendar...",
    "⚡ Powering up neural networks...",
    "🎯 Calibrating conversation skills...",
    "🔧 Fine-tuning responses...",
    "✨ Bringing your assistant to life..."
  ];

  useEffect(() => {
    // Force token refresh immediately
    const refreshToken = async () => {
      try {
        await fetchAuthSession({ forceRefresh: true });
      } catch (error) {
        console.log('Token refresh error:', error);
      }
    };
    refreshToken();
    trackConversion('subscription_complete', '/subscription-complete');

    // Progress animation
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) return 100;
        return prev + (100 / 60); // Complete in 60 seconds
      });
    }, 1000);

    // Message rotation
    const messageInterval = setInterval(() => {
      setCurrentMessage(prev => (prev + 1) % messages.length);
    }, 7500); // Change every 7.5 seconds

    // Check token and redirect after 60 seconds
    const redirectTimer = setTimeout(async () => {
      try {
        // Force another token refresh
        const session = await fetchAuthSession({ forceRefresh: true });
        const token = session.tokens?.idToken;
        const payload = token?.payload;
        
        // Check if we have business_id and active subscription
        if (payload?.['custom:business_id'] && payload?.['custom:subscription_status'] === 'active') {
          navigate('/dashboard');
        } else {
          // If still not ready, wait a bit more and try again
          setTimeout(() => navigate('/dashboard'), 5000);
        }
      } catch (error) {
        console.log('Redirect check error:', error);
        navigate('/dashboard'); // Fallback redirect
      }
    }, 60000);

    return () => {
      clearInterval(progressInterval);
      clearInterval(messageInterval);
      clearTimeout(redirectTimer);
    };
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
        {/* Logo/Icon */}
        <div className="mb-8">
          <img src="/favicon.svg" alt="Remote Assistant" className="w-20 h-20 mx-auto" />
        </div>

        {/* Title */}
        <h2 className="text-2xl font-semibold text-[#222324] mb-2">
          Welcome to Remote Assistant!
        </h2>
        <p className="text-[#222324] text-sm font-light mb-8">
          We're setting up your personalized AI assistant...
        </p>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className="bg-gradient-to-r from-[#055efd] to-[#0449d4] h-3 rounded-full transition-all duration-1000 ease-out"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <p className="text-sm text-gray-500 mt-2 font-light">{Math.round(progress)}% complete</p>
        </div>

        {/* Status Message */}
        <div className="mb-8">
          <p className="text-lg text-[#222324] font-medium animate-pulse">
            {messages[currentMessage]}
          </p>
        </div>

        {/* Animation */}
        <div className="flex justify-center space-x-2 mb-8">
          <div className="w-3 h-3 bg-[#055efd] rounded-full animate-bounce"></div>
          <div className="w-3 h-3 bg-[#0449d4] rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
          <div className="w-3 h-3 bg-[#055efd] rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
        </div>

        {/* Footer */}
        <p className="text-sm text-gray-500 font-light">
          This usually takes about a minute. Please don't close this page.
        </p>
      </div>
    </div>
  );
};

export default SubscriptionComplete;